import { useState } from "react";
import { Stack, TextField, Collapse, Box, Alert, FormControlLabel, Button, Typography } from "@mui/material";

import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDialogs } from "@toolpad/core/useDialogs";
import { resetPassword, signIn, signOut } from "aws-amplify/auth";

import { EMAIL, PASSWORD } from "@helpers/regex";
import { CustomSwitch } from "@components/CustomSwitch";
import { inputConvertToLowercase } from "@helpers/generals";
import { setRecoveryUsername } from "@store/slices/appSlice";
import { loginSuccess, setSignInStep } from "@store/slices/authSlice";
import { Notificacion } from "@components/DialogosSegmentos/Notificacion";

export const FormularioInicioSesion = () => {
    const dialogs = useDialogs();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [error, setError] = useState<boolean>(false);
    const [errroMsg, setErrorMsg] = useState<string>();
    const [rememberMe, setRememberMe] = useState<boolean>(false);

    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: yup.object().shape({
            email: yup
                .string()
                .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)")
                .required("Campo requerido"),
            password: yup
                .string()
                .matches(
                    PASSWORD,
                    "Formato incorrecto. Debe contener una letra mayúscula, número, carácter especial y más de 8 caracteres.",
                )
                .required("Campo requerido"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                setError(false);
                setErrorMsg(undefined);

                const signedResult = await signIn({
                    username: values.email,
                    password: values.password,
                    options: {
                        authFlowType: "USER_SRP_AUTH",
                    },
                });

                if (signedResult.isSignedIn && signedResult.nextStep.signInStep == "DONE") {
                    if (rememberMe) {
                        if (formik.values.email == localStorage.getItem("email")) {
                            localStorage.removeItem("email");
                        }
                    } else {
                        localStorage.setItem("email", formik.values.email);
                    }

                    dispatch(loginSuccess());
                    dispatch(setSignInStep("MFA"));

                    // TODO Para brincar validación OTP
                    // dispatch(verifiedOTP());
                    // dispatch(setSignInStep("WELCOME"));
                    // navigate("/solicitudes");
                } else if (signedResult.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
                    // navigate("/cambiar_contrasenia");
                    dispatch(loginSuccess());
                    dispatch(setSignInStep("NEW_PASSWORD"));
                }
            } catch (error: any) {
                if (error.name === "NotAuthorizedException") {
                    setError(true);
                    setErrorMsg("Credenciales incorrectas. Verifica tu correo electrónico y contraseña.");
                }

                if (error.name === "UserNotFoundException") {
                    setError(true);
                    setErrorMsg("Verifica tu correo electrónico y contraseña.");
                }

                if (error.status === 404) {
                    setError(true);
                    setErrorMsg(error.data.message);
                }

                if (error.status == 401 && error.data.message == "ERREXPSW") {
                    setError(true);
                    setErrorMsg(error.data.errors);
                }

                await signOut();
                setTimeout(() => {
                    setError(false);
                }, 2000);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleRecoveryAccountWithCode = async () => {
        dispatch(setRecoveryUsername(""));
        dispatch(setSignInStep("RESET_PASS"));
        // navigate("/recuperar_cuenta");
    };

    const handleRecoveryAccount = async () => {
        if (formik.values.email.trim().length !== 0 && Boolean(formik.errors.email) == false) {
            await resetPassword({ username: formik.values.email });
            // setShowModalRecoveryCode(true);
            await dialogs.open(Notificacion, {
                type: "INFO",
                text: `Se ha enviado un código de verificación al correo ${formik.values.email}`,
            });
        }
    };

    const handleOnChangeRememberMe = (value: React.ChangeEvent<HTMLInputElement>) => {
        setRememberMe(!value.target.checked);
    };

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <Stack mt={2} direction={"column"} justifyContent={"flex-start"} alignItems={"center"}>
                <Typography variant="h2" fontWeight={700} color="#002652" mb={1}>
                    ¡Hola!
                </Typography>
                <Typography my={3} textAlign={"center"} variant="body1" color="#002652">
                    Ingresa tu correo y tu contraseña
                </Typography>
                <TextField
                    name="email"
                    data-testid="email-login"
                    sx={{ my: 2, width: "60%" }}
                    label="Correo electrónico"
                    required
                    size="small"
                    onChange={(event) => inputConvertToLowercase(event, formik)}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.email && Boolean(formik.errors.email)) ?? false}
                    helperText={formik.touched.email && formik.errors.email}
                    value={formik.values.email}
                />
                <TextField
                    name="password"
                    data-testid="password-login"
                    sx={{ my: 2, width: "60%" }}
                    label="Contraseña"
                    type="password"
                    size="small"
                    required
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.password && Boolean(formik.errors.password)) ?? false}
                    helperText={formik.touched.password && formik.errors.password}
                />
                <Collapse in={error && errroMsg !== undefined}>
                    <Box sx={{ mt: 1 }} justifyContent={"center"} display={"flex"}>
                        <Alert variant="outlined" severity="error" sx={{ textAlign: "center", maxWidth: "80%" }}>
                            {errroMsg}
                        </Alert>
                    </Box>
                </Collapse>
                <Box mt={3}>
                    <Typography
                        variant="body2"
                        sx={{
                            textDecoration: "underline",
                            color: "#1976d2",
                            cursor: "pointer",
                        }}
                        onClick={handleRecoveryAccount}
                    >
                        Olvidé mi contraseña
                    </Typography>
                </Box>
                <Box my={1}>
                    <Typography variant="body2" color="#002652" component={"span"}>
                        Si tienes un código de recuperación,{" "}
                        <Typography
                            variant="body2"
                            component={"span"}
                            sx={{
                                textDecoration: "underline",
                                color: "#1976d2",
                                cursor: "pointer",
                            }}
                            onClick={handleRecoveryAccountWithCode}
                        >
                            ingresa aquí.
                        </Typography>
                    </Typography>
                </Box>
                <Box textAlign={"center"} my={3}>
                    <FormControlLabel
                        sx={{ color: "#002652" }}
                        control={<CustomSwitch onChange={handleOnChangeRememberMe} checked={!rememberMe} />}
                        label={"Recordar usuario en este dispositivo"}
                    />
                </Box>
                <Button
                    type="submit"
                    data-testid="submit-login"
                    variant="contained"
                    fullWidth
                    sx={{ py: 1, mb: 5, mt: 3, maxWidth: "60%" }}
                    loadingPosition="end"
                    loading={formik.isSubmitting}
                >
                    Iniciar sesión
                </Button>
            </Stack>
        </form>
    );
};
